import { z } from 'zod';
import enZodTranslation from 'zod-i18n-map/locales/en/zod.json';
import esZodTranslation from 'zod-i18n-map/locales/es/zod.json';
// Import zod translations directly from zod-i18n-map
import ptZodTranslation from 'zod-i18n-map/locales/pt/zod.json';
/**
 * Map locale codes to zod-i18n-map locale codes
 * zod-i18n-map uses language codes (pt, en, es) instead of locale codes (pt-BR, en-US, es-ES)
 */
function getZodLocale(locale) {
    if (locale.startsWith('pt'))
        return 'pt';
    if (locale.startsWith('en'))
        return 'en';
    if (locale.startsWith('es'))
        return 'es';
    return 'pt'; // fallback
}
/**
 * Get zod translation object for a given locale
 */
function getZodTranslation(locale) {
    const zodLocale = getZodLocale(locale);
    switch (zodLocale) {
        case 'pt':
            return ptZodTranslation;
        case 'en':
            return enZodTranslation;
        case 'es':
            return esZodTranslation;
        default:
            return ptZodTranslation;
    }
}
/**
 * Interpolate a translation string with variables
 */
function interpolate(template, vars, t) {
    let result = template;
    // Replace {{variable}} with values
    result = result.replace(/\{\{(-?\s*)(\w+)(\s*,\s*(\w+))?\}\}/g, (match, prefix, key, _, format) => {
        const value = vars[key];
        if (value === undefined || value === null) {
            return match;
        }
        // Handle array formatting with {{- keys}}
        if (prefix.includes('-') && Array.isArray(value)) {
            return value.join(', ');
        }
        // Handle datetime formatting
        if (format === 'datetime' && value instanceof Date) {
            return value.toISOString();
        }
        return String(value);
    });
    // If we have a translation function, use it for further interpolation
    if (t) {
        // i18next handles interpolation, so we can use it
        return t(result, vars);
    }
    return result;
}
/**
 * Get translation key for a Zod v4 issue
 */
function getTranslationKey(issue) {
    const code = issue.code;
    // Handle custom messages with translation keys
    // If message starts with "admin:validation.", it's a custom translation key
    if (issue.message &&
        typeof issue.message === 'string' &&
        issue.message.startsWith('admin:validation.')) {
        return issue.message.replace('admin:', '');
    }
    if (!code) {
        return 'errors.custom';
    }
    // Handle invalid_format (Zod v4 uses this instead of invalid_string)
    if (code === 'invalid_format') {
        const format = issue.format;
        if (format === 'starts_with') {
            return 'errors.invalid_string.startsWith';
        }
        if (format === 'ends_with') {
            return 'errors.invalid_string.endsWith';
        }
        if (format === 'email') {
            return 'errors.invalid_string.email';
        }
        if (format === 'url') {
            return 'errors.invalid_string.url';
        }
        if (format === 'uuid') {
            return 'errors.invalid_string.uuid';
        }
        if (format === 'cuid') {
            return 'errors.invalid_string.cuid';
        }
        if (format === 'regex') {
            return 'errors.invalid_string.regex';
        }
        if (format === 'datetime') {
            return 'errors.invalid_string.datetime';
        }
        return 'errors.invalid_string.regex'; // fallback
    }
    // Handle too_small and too_big
    if (code === 'too_small' || code === 'too_big') {
        const typedIssue = issue;
        const origin = typedIssue.origin || 'string';
        const mode = typedIssue.inclusive ? 'inclusive' : 'not_inclusive';
        const exact = typedIssue.exact ? 'exact' : mode;
        // Map origin to translation key
        const originMap = {
            string: 'string',
            array: 'array',
            number: 'number',
            int: 'number',
            bigint: 'number',
            date: 'date',
            set: 'set',
        };
        const type = originMap[origin] || 'string';
        return `errors.${code}.${type}.${exact}`;
    }
    // Handle invalid_type with received type
    if (code === 'invalid_type') {
        const typedIssue = issue;
        const input = typedIssue.input;
        if (input === undefined) {
            return 'errors.invalid_type_received_undefined';
        }
        if (input === null) {
            return 'errors.invalid_type_received_null';
        }
        return 'errors.invalid_type';
    }
    // Handle unrecognized_keys
    if (code === 'unrecognized_keys') {
        return 'errors.unrecognized_keys';
    }
    // Handle invalid_enum_value (mapped from invalid_value in v4)
    if (code === 'invalid_value') {
        return 'errors.invalid_enum_value';
    }
    // Handle not_multiple_of
    if (code === 'not_multiple_of') {
        return 'errors.not_multiple_of';
    }
    // Default to errors.{code}
    return `errors.${code}`;
}
/**
 * Get type translation
 */
function getTypeTranslation(type, translation) {
    return translation.types[type] || type;
}
/**
 * Create a custom error map for Zod v4
 */
function createZodErrorMap(translation, t) {
    return (issue) => {
        const key = getTranslationKey(issue);
        // If it's a custom admin validation key, use i18next to translate
        if (key.startsWith('validation.')) {
            if (t) {
                const translated = t(key, { ns: 'admin' });
                if (translated !== key) {
                    return { message: translated };
                }
            }
        }
        const translationPath = key.split('.');
        // Navigate through translation object
        let message;
        let current = translation;
        for (const part of translationPath) {
            if (current && typeof current === 'object' && part in current) {
                current = current[part];
            }
            else {
                current = undefined;
                break;
            }
        }
        if (typeof current === 'string') {
            message = current;
        }
        else {
            // Fallback to default error message
            message = 'Entrada inválida';
        }
        // Prepare variables for interpolation
        const vars = {};
        // Add common variables
        if ('expected' in issue && issue.expected) {
            const expected = issue.expected;
            vars.expected = getTypeTranslation(expected, translation);
        }
        // Get received type from input
        if (issue.input !== undefined && issue.input !== null) {
            const receivedType = typeof issue.input;
            vars.received = getTypeTranslation(receivedType, translation);
        }
        // Add issue-specific variables
        if (issue.code === 'too_small') {
            const typedIssue = issue;
            if ('minimum' in typedIssue)
                vars.minimum = typedIssue.minimum;
        }
        if (issue.code === 'too_big') {
            const typedIssue = issue;
            if ('maximum' in typedIssue)
                vars.maximum = typedIssue.maximum;
        }
        if (issue.code === 'invalid_format') {
            const typedIssue = issue;
            if (typedIssue.format === 'starts_with' && typedIssue.prefix) {
                vars.startsWith = typedIssue.prefix;
            }
            if (typedIssue.format === 'ends_with' && typedIssue.suffix) {
                vars.endsWith = typedIssue.suffix;
            }
        }
        if (issue.code === 'unrecognized_keys') {
            const typedIssue = issue;
            vars.keys = typedIssue.keys;
        }
        if (issue.code === 'invalid_value') {
            const typedIssue = issue;
            if (typedIssue.values && typedIssue.values.length > 0) {
                vars.received = String(typedIssue.values[0]);
                vars.options = typedIssue.values;
            }
        }
        if (issue.code === 'not_multiple_of') {
            const typedIssue = issue;
            vars.multipleOf = typedIssue.divisor;
        }
        // Interpolate the message
        const finalMessage = interpolate(message, vars, t);
        return { message: finalMessage };
    };
}
/**
 * Configure Zod error messages for a specific locale
 * This should be called whenever the locale changes
 *
 * @param locale - The locale to use for error messages
 * @param i18nInstance - Optional i18next instance (for frontend/backend)
 */
export function configureZodI18n(locale, i18nInstance) {
    const translation = getZodTranslation(locale);
    // Create error map with translation function if available
    const errorMap = createZodErrorMap(translation, i18nInstance?.t);
    z.setErrorMap(errorMap);
}
/**
 * Initialize Zod i18n with default locale
 * This is a convenience function for initial setup
 */
export function initZodI18n(locale = 'pt-BR') {
    configureZodI18n(locale);
}
//# sourceMappingURL=zodI18n.js.map